/**
 * Money formatting. The API speaks INTEGER satang; the UI speaks baht.
 * Mirrors functions/_lib/money.ts — display rules only, no arithmetic that
 * matters lives here.
 */

export const toBaht = (satang) => (satang ?? 0) / 100;

/** ฿1,250,000 — satang shown only when they are actually non-zero. */
export function thb(satang, opts = {}) {
  const s = satang ?? 0;
  const decimals = opts.decimals ?? s % 100 !== 0;
  return (
    '฿' +
    toBaht(s).toLocaleString('en-US', {
      minimumFractionDigits: decimals ? 2 : 0,
      maximumFractionDigits: decimals ? 2 : 0,
    })
  );
}

/** ฿1.25M / ฿850K — for dense tiles and kanban column totals. */
export function thbShort(satang) {
  const b = toBaht(satang ?? 0);
  const abs = Math.abs(b);
  if (abs >= 1_000_000) return `฿${(b / 1_000_000).toFixed(b % 1_000_000 === 0 ? 0 : 1)}M`;
  if (abs >= 1_000) return `฿${Math.round(b / 1_000)}K`;
  return thb(satang);
}

/** "฿1,250,000" typed by a human -> satang. */
export function parseTHB(input) {
  if (input === null || input === undefined || input === '') return 0;
  const cleaned = String(input).replace(/[^0-9.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/** "3 Aug" / "3 Aug 2025" — drops the year when it's the current one. */
export function shortDate(iso, lang = 'en') {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(lang === 'th' ? 'th-TH' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'Asia/Bangkok',
  });
}

/**
 * Periods are `YYYY-MM` in **Bangkok time**, matching `periodBangkok()` on the
 * server. Using the browser's own month would put a rep in a different month to
 * the API for the first seven hours of every month — the screen would ask for a
 * period the server considers the future and get a 400.
 */
export function periodNow() {
  return new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 7);
}

/** Step a period by whole months. `shiftPeriod('2026-01', -1)` → `2025-12`. */
export function shiftPeriod(period, delta) {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** "August 2026" / "สิงหาคม 2026". */
export function periodLabel(period, lang = 'en') {
  const [y, m] = period.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(lang === 'th' ? 'th-TH' : 'en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function relTime(iso, lang = 'en') {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  const th = lang === 'th';
  if (mins < 1) return th ? 'เมื่อครู่' : 'just now';
  if (mins < 60) return th ? `${mins} นาทีที่แล้ว` : `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return th ? `${hrs} ชม.ที่แล้ว` : `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return th ? `${days} วันที่แล้ว` : `${days}d ago`;
  return shortDate(iso, lang);
}
